import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Typography } from "@material-ui/core";

import ModalSolicitud from "../components/ModalSolicitud";
import PasosSolicitud from "../components/PasosSolicitud";
import PasoFecha from "./pasos/PasoFecha";
import PasoClase from "./pasos/PasoClase";
import PasoReserva from "./pasos/PasoReserva";
import PasoEstudiantes from "./pasos/PasoEstudiantes";
import PasoMaterial from "./pasos/PasoMaterial";

import { useCamposFormulario } from "../hooks/useCamposFormulario";
import { useConsulta } from "../hooks/useConsulta";
import { useListaEditable } from "../hooks/useListaEditable";
import { usePeriodoVigente } from "../hooks/usePeriodoVigente";

import { TIPO_ARIA, TIPO_LIBROS } from "../constants";
import {
  CAMPO_MATERIA,
  VALORES_INICIALES,
  valoresDesdeSolicitud,
} from "../model/campos";
import {
  PASO_CLASE,
  PASO_FECHA,
  PASO_ESTUDIANTES,
  PASO_MATERIAL,
  PASO_RESERVA,
  pasosDe,
  primerPasoConError,
} from "../model/pasos";
import {
  perfilDe,
  puedeVerLogistica,
  seInscribeEnSuSolicitud,
} from "../model/permisos";
import {
  construirPayload,
  esValido,
  estudiantesIniciales,
  hayErrores,
  nombreCompleto,
} from "../model/solicitud";
import {
  obtenerBloques,
  obtenerHorarioSolicitudes,
  obtenerEstudiantesDeGrupo,
  obtenerMaterias,
  obtenerNivelesAria,
  obtenerSalones,
  revisarNivelesVistos,
} from "../services/bibliotecaApi";

// Al cambiar el bloque, el salon elegido deja de ser valido.
const DEPENDIENTES = { id_bloque: ["id_salon"] };

const texto = (valor) => {
  const limpio = `${valor}`.trim();
  return limpio || null;
};

const mismo = (a, b) => `${a}` === `${b}`;

/**
 * Formulario de solicitud, comun a los dos tipos, en un modal con pasos como
 * la Solicitud de grado: un paso a la vez, para que no se vea cargado. Que
 * pasos hay y como se valida cada uno lo decide model/pasos.js; los campos,
 * model/campos.js; los catalogos y el envio, services/bibliotecaApi.js.
 *
 * Todo el estado vive aqui y no en los pasos: moverse entre ellos no pierde
 * lo que ya se lleno. Los pasos solo pintan lo que reciben.
 *
 * Con `solicitud` abre en modo edicion: los mismos pasos y las mismas reglas,
 * arrancando con lo que quedo guardado. El tipo no se toca, porque cada uno
 * vive en su propia tabla.
 */
const SolicitudForm = ({
  tipo,
  titulo,
  usuario,
  solicitud,
  onCerrar,
  onGuardar,
  onMensaje,
  animarEntrada,
}) => {
  const perfil = perfilDe(usuario);
  const esAria = tipo === TIPO_ARIA;
  const conLogistica = puedeVerLogistica(perfil, tipo);
  const identificacion = usuario?.identificacion ?? null;
  const nombrePropio = nombreCompleto(usuario);
  const inscribirSolicitante = seInscribeEnSuSolicitud(perfil);
  const esEdicion = Boolean(solicitud);

  const pasos = useMemo(() => pasosDe(perfil, tipo), [perfil, tipo]);
  const [paso, setPaso] = useState(0);
  const pasoActual = pasos[paso];
  const esUltimo = paso === pasos.length - 1;

  const [enviando, setEnviando] = useState(false);
  // Al editar manda la lista de estudiantes que quedo guardada: solo se
  // reemplaza por la del grupo cuando el docente elige otra materia.
  const [materiaTocada, setMateriaTocada] = useState(!esEdicion);

  const { periodo, cargando: cargandoPeriodo } = usePeriodoVigente();
  const iniciales = useMemo(
    () => (solicitud ? valoresDesdeSolicitud(solicitud) : VALORES_INICIALES),
    [solicitud],
  );
  const { valores, errores, setErrores, onChange } = useCamposFormulario(
    iniciales,
    DEPENDIENTES,
  );

  const onChangeCampo = (evento) => {
    if (evento.target.name === CAMPO_MATERIA.name) setMateriaTocada(true);
    onChange(evento);
  };

  const { datos: niveles, cargando: cargandoNiveles } = useConsulta(obtenerNivelesAria, []);

  // Bloques y salones son genericas de Emma: el salon sale de la relacion con
  // su bloque, igual que en el AGIL viejo.
  const { datos: bloques } = useConsulta(obtenerBloques, []);
  // El horario de atencion, para mostrarlo en Reserva. Si falla, no se
  // muestra: el backend igual lo valida al enviar.
  const { datos: horario } = useConsulta(obtenerHorarioSolicitudes, [], null);
  const { datos: salones } = useConsulta(
    () => obtenerSalones(valores.id_bloque),
    [valores.id_bloque],
  );

  // Las materias del docente si vienen de SICUC.
  const {
    datos: materias,
    cargando: cargandoMaterias,
    error: errorMaterias,
  } = useConsulta(
    () => obtenerMaterias(conLogistica ? identificacion : null),
    [identificacion, conLogistica],
  );

  const materiaSeleccionada =
    materias.find(({ id }) => mismo(id, valores[CAMPO_MATERIA.name])) ?? null;
  const grupo = materiaSeleccionada ? materiaSeleccionada.cod_grupo : null;

  // La materia guardada se reconoce por su codigo y su grupo: el select
  // trabaja con el id de SICUC, que no viaja en la solicitud. Si el docente
  // ya no dicta esa materia, el select queda vacio y tiene que elegir otra.
  const materiaPrecargada = useRef(false);
  useEffect(() => {
    if (materiaPrecargada.current || !solicitud || materias.length === 0) return;
    materiaPrecargada.current = true;

    const guardada = materias.find(
      ({ cod_materia, grupo: grupoDeLaMateria }) =>
        cod_materia === solicitud.cod_materia && grupoDeLaMateria === solicitud.grupo,
    );
    if (guardada) {
      onChange({ target: { name: CAMPO_MATERIA.name, value: guardada.id } });
    }
  }, [solicitud, materias, onChange]);

  const catalogos = { materias, bloques, salones };

  // Los estudiantes llegan del buscador de Emma, ya como persona: de ahi se
  // guardan la cedula y el nombre, que es lo que viaja en la solicitud.
  const estudiantes = useListaEditable((persona) =>
    persona?.identificacion
      ? {
          identificacion: `${persona.identificacion}`,
          nombre: nombreCompleto(persona),
        }
      : null,
  );

  const libros = useListaEditable((valor) => {
    const dato = texto(valor);
    return dato ? { nombre: dato } : null;
  });

  const nivelesElegidos = useListaEditable((valor) => {
    const nivel = niveles.find(({ id }) => mismo(id, valor));
    if (!nivel) return null;

    return {
      id_capacitacion: nivel.id,
      nombre: nivel.nombre,
      duracion: nivel.duracion,
    };
  });

  // Estudiantes matriculados en el grupo de la materia elegida.
  const { datos: estudiantesGrupo, cargando: cargandoEstudiantes } = useConsulta(
    () => obtenerEstudiantesDeGrupo(grupo),
    [grupo],
  );

  const { reemplazar: reemplazarEstudiantes } = estudiantes;
  const { reemplazar: reemplazarLibros } = libros;
  const { reemplazar: reemplazarNiveles } = nivelesElegidos;

  useEffect(() => {
    // Los estudiantes arrancan con lo guardado, si se edita, y con quien
    // solicita de primero si es estudiante: queda inscrito siempre.
    reemplazarEstudiantes(
      estudiantesIniciales({
        guardados: solicitud?.estudiantes,
        identificacion,
        nombre: nombrePropio,
        inscribirSolicitante,
      }),
    );
  }, [solicitud, identificacion, nombrePropio, inscribirSolicitante, reemplazarEstudiantes]);

  useEffect(() => {
    // Al editar, libros y niveles arrancan con lo que quedo guardado.
    if (!solicitud) return;

    reemplazarLibros(solicitud.libros);
    reemplazarNiveles(
      solicitud.niveles.map(({ nivel, duracion }) => ({
        id_capacitacion: nivel?.id,
        nombre: nivel?.nombre,
        duracion,
      })),
    );
  }, [solicitud, reemplazarLibros, reemplazarNiveles]);

  useEffect(() => {
    // Al cambiar de materia, la lista pasa a ser la de su grupo; despues se
    // pueden seguir agregando o quitando estudiantes a mano.
    if (grupo && materiaTocada) reemplazarEstudiantes(estudiantesGrupo);
  }, [grupo, estudiantesGrupo, reemplazarEstudiantes, materiaTocada]);

  // En ARIA, quienes de los marcados ya tienen cada nivel este periodo. Se
  // consulta al cambiar la lista o los niveles; las claves evitan repetir la
  // consulta cuando no cambio nada.
  const claveEstudiantes = estudiantes.seleccionados.map(({ identificacion: i }) => i).join(",");
  const claveNiveles = nivelesElegidos.items.map(({ id_capacitacion: i }) => i).join(",");
  const { datos: nivelesVistos } = useConsulta(
    () =>
      esAria && claveEstudiantes && claveNiveles
        ? revisarNivelesVistos({
            estudiantes: claveEstudiantes.split(","),
            niveles: claveNiveles.split(","),
            excluir: solicitud?.id,
          })
        : Promise.resolve(null),
    [esAria, claveEstudiantes, claveNiveles, solicitud?.id],
    null,
  );
  const nivelesBloqueados = (nivelesVistos?.niveles ?? [])
    .filter(({ bloquea }) => bloquea)
    .map(({ id }) => nivelesElegidos.items.find((n) => mismo(n.id_capacitacion, id))?.nombre)
    .filter(Boolean);

  // Lo que validan los pasos y lo que se envia: siempre la foto del momento.
  const partes = () => ({
    nivelesBloqueados,
    perfil,
    tipo,
    valores,
    // Se envian solo los marcados; los demas siguen visibles en la lista.
    estudiantes: estudiantes.seleccionados,
    libros: libros.items,
    capacitaciones: nivelesElegidos.items,
  });

  // Muestra lo que le falta a un paso: los campos en rojo y, para las listas,
  // un aviso.
  const senalar = ({ errores: encontrados, mensaje }) => {
    if (hayErrores(encontrados)) setErrores(encontrados);
    if (mensaje) onMensaje(mensaje);
  };

  // Se valida al pedir el siguiente paso, en vez de deshabilitar el boton: un
  // boton gris no dice que falta.
  const siguiente = () => {
    const resultado = pasoActual.validar(partes());
    if (!esValido(resultado)) {
      senalar(resultado);
      return;
    }
    setPaso((actual) => actual + 1);
  };

  const atras = () => setPaso((actual) => Math.max(actual - 1, 0));

  const enviar = async () => {
    if (enviando) return;

    // Se revisan todos los pasos, no solo el ultimo: si alguno quedo
    // incompleto, se vuelve a el y se senala que falta.
    const datos = partes();
    const { indice, resultado } = primerPasoConError(pasos, datos);
    if (indice >= 0) {
      setPaso(indice);
      senalar(resultado);
      return;
    }

    setEnviando(true);
    const guardada = await onGuardar(
      construirPayload({ ...datos, materia: materiaSeleccionada }),
    );
    // Si se guardo, quien abrio el modal lo cierra y lo desmonta: no se debe
    // tocar su estado.
    if (!guardada) setEnviando(false);
  };

  // Al editar se muestra el periodo con el que se radico: ese no cambia.
  const periodoSolicitud = solicitud?.periodo ?? null;
  const periodoTexto =
    periodoSolicitud ??
    (cargandoPeriodo ? "Cargando..." : periodo || "No definido");
  const sinPeriodo = !periodoSolicitud && !cargandoPeriodo && !periodo;

  const textoEnviar = () => {
    if (enviando) return esEdicion ? "Guardando..." : "Enviando...";
    return esEdicion ? "Guardar cambios" : "Enviar";
  };

  const avisoMaterias = () => {
    if (cargandoMaterias) return "Consultando tus materias...";
    if (errorMaterias) return "No fue posible consultar las materias";
    if (materias.length === 0) return "No tienes materias en el periodo vigente";
    return "";
  };

  const avisoEstudiantes = () => {
    if (cargandoEstudiantes) return "Cargando los estudiantes del grupo...";
    if (grupo) return "Desmarca a quien no asista; puedes buscar a otros";
    return "";
  };

  // El salon depende del bloque: mientras no se elija, la lista esta vacia.
  const ayudaDe = (campo) =>
    campo === "id_salon" && !valores.id_bloque ? "Elige primero el bloque" : "";

  const contenidoDelPaso = () => {
    switch (pasoActual.id) {
      case PASO_FECHA:
        return (
          <PasoFecha
            valores={valores}
            errores={errores}
            onChange={onChangeCampo}
          />
        );
      case PASO_CLASE:
        return (
          <PasoClase
            valores={valores}
            errores={errores}
            onChange={onChangeCampo}
            catalogos={catalogos}
            ayuda={avisoMaterias()}
          />
        );
      case PASO_RESERVA:
        return (
          <PasoReserva
            valores={valores}
            errores={errores}
            onChange={onChangeCampo}
            catalogos={catalogos}
            ayudaDe={ayudaDe}
            horarioAtencion={horario?.atencion.texto ?? ""}
          />
        );
      case PASO_ESTUDIANTES:
        return (
          <PasoEstudiantes
            estudiantes={estudiantes}
            cargando={cargandoEstudiantes}
            ayuda={avisoEstudiantes()}
            onMensaje={onMensaje}
          />
        );
      case PASO_MATERIAL:
        return (
          <PasoMaterial
            esAria={esAria}
            libros={libros}
            nivelesElegidos={nivelesElegidos}
            niveles={niveles}
            cargandoNiveles={cargandoNiveles}
            nivelesVistos={nivelesVistos}
          />
        );
      default:
        return null;
    }
  };

  const acciones = (
    <>
      <Button onClick={atras} disabled={paso === 0 || enviando}>
        Atrás
      </Button>
      {esUltimo ? (
        <Button color="primary" variant="contained" onClick={enviar} disabled={enviando}>
          {textoEnviar()}
        </Button>
      ) : (
        <Button color="primary" onClick={siguiente} disabled={enviando}>
          Siguiente
        </Button>
      )}
    </>
  );

  return (
    <ModalSolicitud
      titulo={titulo}
      onCerrar={onCerrar}
      acciones={acciones}
      animarEntrada={animarEntrada}
    >
      <PasosSolicitud etiquetas={pasos.map(({ etiqueta }) => etiqueta(tipo))} activo={paso} />

      {/* Periodo y solicitante no se llenan: se muestran en todos los pasos.
          La solicitud queda siempre a nombre de quien la crea. */}
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        <strong>Periodo:</strong> {periodoTexto}
        {" · "}
        <strong>Solicitante:</strong> {nombrePropio}
      </Typography>
      {sinPeriodo && (
        <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
          No hay periodo configurado para biblioteca
        </Typography>
      )}

      {contenidoDelPaso()}
    </ModalSolicitud>
  );
};

SolicitudForm.propTypes = {
  tipo: PropTypes.oneOf([TIPO_LIBROS, TIPO_ARIA]).isRequired,
  // Titulo del modal: el nombre del tipo, o "Editar ..." al editar.
  titulo: PropTypes.string.isRequired,
  onCerrar: PropTypes.func.isRequired,
  // Debe devolver una Promise<boolean>: true si la solicitud se guardo.
  onGuardar: PropTypes.func.isRequired,
  usuario: PropTypes.object,
  // Solo al editar: la solicitud como la entrega el detalle del backend.
  solicitud: PropTypes.object,
  onMensaje: PropTypes.func,
  // En false, el modal aparece sin animacion (ver ModalSolicitud).
  animarEntrada: PropTypes.bool,
};

SolicitudForm.defaultProps = {
  onMensaje: () => {},
  animarEntrada: true,
};

export default SolicitudForm;
